import { Check, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslate } from "@refinedev/core";

import type { Antwoord, DierAntwoorden, Groep } from "./types";

/**
 * De antwoorden van de eigenaar, om te beoordelen (mockup Maarten, 27-10-2026).
 *
 * <p><b>Links de onderdelen, rechts de vragen.</b> Een vragenlijst heeft tientallen vragen in een
 * handvol onderdelen; die allemaal onder elkaar zetten maakt beoordelen tot scrollen. Links zie je
 * in één blik waar nog werk ligt, rechts lees je één onderdeel uit.
 *
 * <p><b>Niets over de vragen is hier vastgelegd.</b> De organisatie bedenkt haar eigen vragen, in
 * haar eigen volgorde en groepen (Maarten, 27-10-2026). Dit scherm toont wat de vragenlijst zegt:
 * de groepstitels zijn haar titels, de nummering is haar volgorde, en een vraag die zij morgen
 * toevoegt verschijnt hier zonder dat er iets aangepast hoeft te worden.
 *
 * <p><b>Beoordelen is per vraag.</b> "Akkoord" of "Aandachtspunt" — en nog eens klikken haalt het
 * oordeel weer weg, want wie zich vergist hoort dat te kunnen rechtzetten.
 */

export function WachtlijstAntwoorden({ antwoorden, meerdereDieren, onBeoordeel, bezig }: {
  antwoorden: DierAntwoorden[];
  meerdereDieren: boolean;
  onBeoordeel: (dierId: number, veldSleutel: string, oordeel: string | null) => void;
  bezig: boolean;
}) {
  const t = useTranslate();
  const [gekozen, setGekozen] = useState(0);

  // Eén lijst van onderdelen over alle dieren heen; bij meerdere dieren krijgt elk onderdeel het
  // dier erbij, want dezelfde vraag kan voor Bobby anders beantwoord zijn dan voor Mimi.
  const onderdelen = useMemo(
    () => antwoorden.flatMap((d) => d.groepen.map((g) => ({ dier: d, groep: g }))),
    [antwoorden],
  );

  if (onderdelen.length === 0) {
    return <p className="fawkes-muted">{t("wachtlijst.geenTelling")}</p>;
  }

  const huidig = onderdelen[Math.min(gekozen, onderdelen.length - 1)];
  const vragen = huidig.groep.antwoorden;
  const beoordeeld = vragen.filter((a) => a.oordeel).length;
  const aandacht = vragen.filter((a) => a.oordeel === "AANDACHT").length;

  return (
    <div className="wl-beoordeel">
      <nav className="wl-beoordeel__onderdelen" aria-label={t("wachtlijstDossier.onderdelen")}>
        <p className="wl-beoordeel__kop">{t("wachtlijstDossier.onderdelen")}</p>
        <p className="fawkes-muted wl-beoordeel__uitleg">{t("wachtlijstDossier.onderdelenUitleg")}</p>
        <ol>
          {onderdelen.map((o, i) => {
            const klaarAantal = o.groep.antwoorden.filter((a) => a.oordeel).length;
            const heeftAandacht = o.groep.antwoorden.some((a) => a.oordeel === "AANDACHT");
            return (
              <li key={`${o.dier.dierId}:${o.groep.titel}`}>
                <button type="button" aria-current={i === gekozen}
                        className={`wl-beoordeel__onderdeel${i === gekozen ? " is-gekozen" : ""}`}
                        onClick={() => setGekozen(i)}>
                  <span className="wl-beoordeel__nr" aria-hidden="true">{i + 1}</span>
                  <span className="wl-beoordeel__titel">
                    {o.groep.titel}
                    {meerdereDieren && <small>{o.dier.dierNaam ?? t("wachtlijstDossier.dierZonderNaam")}</small>}
                  </span>
                  <span className={`wl-beoordeel__telling${heeftAandacht ? " is-aandacht" : ""}`}>
                    {klaarAantal}/{o.groep.antwoorden.length}
                    {heeftAandacht && <span aria-hidden="true"> !</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      <section className="wl-beoordeel__blad">
        <header className="wl-beoordeel__bladkop">
          <h3>{gekozen + 1}. {huidig.groep.titel}</h3>
          <div className="wl-beoordeel__stand">
            <span>{t("wachtlijstDossier.beoordeeldVan", {
              args: [String(beoordeeld), String(vragen.length)],
            })}</span>
            <div className="wl-balk" aria-hidden="true">
              <span style={{ width: `${vragen.length === 0 ? 0 : (beoordeeld / vragen.length) * 100}%` }} />
            </div>
            <strong className={aandacht > 0 ? "is-aandacht" : undefined}>
              {aandacht > 0
                ? t("wachtlijstDossier.aandachtspunten", { args: [String(aandacht)] })
                : t("wachtlijstDossier.geenAandachtspunten")}
            </strong>
          </div>
        </header>
        <p className="fawkes-muted wl-dossier__uitleg">{t("wachtlijstDossier.beoordeelUitleg")}</p>

        <table className="wl-beoordeel__tabel">
          <thead>
            <tr>
              <th scope="col" className="wl-beoordeel__kolomnr"><span className="fawkes-visually-hidden">#</span></th>
              <th scope="col">{t("wachtlijstDossier.vraagVanHetAsiel")}</th>
              <th scope="col">{t("wachtlijstDossier.antwoordVanDeEigenaar")}</th>
              <th scope="col">{t("wachtlijstDossier.beoordeling")}</th>
            </tr>
          </thead>
          <tbody>
            {vragen.map((a, i) => (
              <Rij
                key={a.veldSleutel}
                nummer={i + 1}
                antwoord={a}
                bezig={bezig}
                onBeoordeel={(oordeel) => onBeoordeel(huidig.dier.dierId, a.veldSleutel, oordeel)}
              />
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Rij({ nummer, antwoord, bezig, onBeoordeel }: {
  nummer: number;
  antwoord: Antwoord;
  bezig: boolean;
  onBeoordeel: (oordeel: string | null) => void;
}) {
  const t = useTranslate();
  const leeg = !antwoord.waarde?.trim();
  return (
    <tr className={antwoord.oordeel === "AANDACHT" ? "is-aandacht" : undefined}>
      <td className="wl-beoordeel__kolomnr">{String(nummer).padStart(2, "0")}</td>
      <th scope="row">
        {antwoord.label}
        {antwoord.verplicht && <span aria-hidden="true"> *</span>}
      </th>
      <td className={leeg ? "fawkes-muted" : undefined}>
        {leeg
          ? (antwoord.verplicht ? t("wachtlijstDetail.nietIngevuld") : "—")
          : antwoord.waarde}
      </td>
      <td>
        <div className="wl-beoordeel__knoppen">
          {/* Nog eens klikken haalt het oordeel weg; dat is hoe je een vergissing rechtzet zonder
              een derde knop voor "toch niet". */}
          <button type="button" disabled={bezig}
                  className={`wl-oordeel wl-oordeel--akkoord${antwoord.oordeel === "AKKOORD" ? " is-aan" : ""}`}
                  onClick={() => onBeoordeel(antwoord.oordeel === "AKKOORD" ? null : "AKKOORD")}>
            <Check size={14} aria-hidden="true" />
            {t("wachtlijstDossier.akkoord")}
          </button>
          <button type="button" disabled={bezig}
                  className={`wl-oordeel wl-oordeel--aandacht${antwoord.oordeel === "AANDACHT" ? " is-aan" : ""}`}
                  onClick={() => onBeoordeel(antwoord.oordeel === "AANDACHT" ? null : "AANDACHT")}>
            <TriangleAlert size={14} aria-hidden="true" />
            {t("wachtlijstDossier.aandachtspunt")}
          </button>
        </div>
      </td>
    </tr>
  );
}

export type { Groep };
